import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Input,
  Modal,
  Shimmer,
  Tabs,
  useToast,
} from '@teals/ui-web';
import { useAuth } from '../../hooks';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import {
  useAskWorkflowRunMutation,
  useCancelWorkflowRunMutation,
  useCreateWorkflowMutation,
  useDiscardWorkflowDraftMutation,
  useGetAgentsQuery,
  useGetWorkflowQuery,
  useGetWorkflowRunQuery,
  useGetWorkflowRunsQuery,
  useGetWorkflowVersionsQuery,
  useLazyGetBuilderTurnQuery,
  useRespondToWorkflowRunMutation,
  useRestoreWorkflowVersionMutation,
  useSaveWorkflowDraftMutation,
  useSendBuilderMessageMutation,
  useSetWorkflowStatusMutation,
  useStartWorkflowRunMutation,
  workflowErrorIssues,
  workflowErrorMessage,
  type BuilderTurn,
  type ReadinessIssue,
  type RunDetail,
  type RunStep,
  type WorkflowDetail as WorkflowDetailData,
  type WorkflowDocument,
  type WorkflowNode,
} from '../../store/api';
import WorkflowGraph, { type StepPaint } from './WorkflowGraph';
import { LIFECYCLE_BADGES } from './Workflows';
import { agentBrief, documentChanges, runVerdict, stepThatMatters, triggerWords } from './graphLayout';

const Page = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Back = styled(Link)`
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-decoration: none;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  margin: 8px 0 16px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin: 0;
  color: var(--text-primary);
`;

const Sub = styled.p`
  margin: 4px 0 0;
  color: var(--text-secondary);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
`;

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface-muted);
  color: var(--text-primary);
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 16px;
  margin-top: 16px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const NewColumns = styled.div<{ $proposing: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${({ $proposing }) => ($proposing ? '420px' : '340px')};
  gap: 16px;
  margin-top: 16px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled(Card)`
  align-self: start;
`;

const Heading = styled.h3`
  margin: 16px 0 6px;
  font-size: 0.8125rem;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  &:first-child {
    margin-top: 0;
  }
`;

const Body = styled.p`
  margin: 0 0 8px;
  color: var(--text-primary);
  font-size: 0.875rem;
  white-space: pre-wrap;
`;

const Pre = styled.pre`
  margin: 0 0 8px;
  padding: 8px;
  max-height: 220px;
  overflow: auto;
  border-radius: 6px;
  background: var(--surface-muted);
  color: var(--text-primary);
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Issue = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px;
  margin-bottom: 6px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: none;
  color: var(--text-primary);
  font-size: 0.8125rem;
  cursor: pointer;
  small {
    display: block;
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-surface);
  color: var(--text-primary);
  font: inherit;
  box-sizing: border-box;
`;

const CodeArea = styled(TextArea)`
  min-height: 420px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 0.75rem;
`;

const NativeSelect = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-surface);
  color: var(--text-primary);
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Message = styled.div<{ $mine: boolean }>`
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 0.875rem;
  white-space: pre-wrap;
  color: var(--text-primary);
  background: ${(props) => (props.$mine ? 'var(--surface-muted)' : 'transparent')};
`;

const RunBar = styled(Bar)`
  align-items: flex-start;
`;

const Verdict = styled.div`
  font-weight: 600;
  margin-top: 6px;
`;

const AgentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;
  color: var(--text-primary);
  small {
    display: block;
    color: var(--text-muted);
  }
`;

const ACTIVE = ['queued', 'running', 'waiting'];
const TEST_WARNING =
  'Agents and their connections are real on a test, so a step that emails someone sends that email.';

function pretty(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

const undoToast = (message: string, onClick: () => void) => ({
  message,
  type: 'success' as const,
  action: { label: 'Undo', onClick },
});

// ---------------------------------------------------------------- Workflow Builder

interface BuilderProps {
  workflow?: WorkflowDetailData;
  focus?: string | null;
  chosenAgents?: string[];
  onProposal: (document: WorkflowDocument | null, turn: BuilderTurn | null) => void;
}

const Builder: React.FC<BuilderProps> = ({ workflow, focus, chosenAgents, onProposal }) => {
  const [sendMessage] = useSendBuilderMessageMutation();
  const [readTurn] = useLazyGetBuilderTurnQuery();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [messages, setMessages] = useState<{ mine: boolean; text: string }[]>([]);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    setBusy(true);
    setMessages((list) => [...list, { mine: true, text }]);
    try {
      let turn = await sendMessage({
        message: text,
        conversation_id: conversationId,
        workflow_id: workflow?.id,
        focus_node: focus || undefined,
        chosen_agents: chosenAgents?.length ? chosenAgents : undefined,
      }).unwrap();
      setConversationId(turn.conversation_id);
      // A proposal can outlast the API gateway, so the reply is read when it is ready.
      for (let tries = 0; tries < 90 && mounted.current; tries += 1) {
        if (turn.status === 'succeeded' || turn.status === 'failed') break;
        await new Promise((resolve) => setTimeout(resolve, 2000));
        turn = await readTurn(turn.id).unwrap();
      }
      if (!mounted.current) return;
      const reply =
        turn.status === 'failed'
          ? turn.error || 'The builder failed. Try again.'
          : turn.reply || 'Done.';
      const warnings = turn.warnings?.length ? `\n\n${turn.warnings.join(' ')}` : '';
      setMessages((list) => [...list, { mine: false, text: reply + warnings }]);
      onProposal(turn.proposal, turn);
    } catch (error) {
      setMessages((list) => [...list, { mine: false, text: workflowErrorMessage(error) }]);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <div>
      <Heading>Workflow Builder</Heading>
      {messages.map((item, index) => (
        <Message key={index} $mine={item.mine}>
          {item.text}
        </Message>
      ))}
      {busy && <Body>Working on it…</Body>}
      {focus && <Body>About {focus}</Body>}
      <TextArea
        aria-label="Describe the job"
        value={draft}
        placeholder={workflow ? 'Describe a change' : 'Describe the job this workflow should do'}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) send();
        }}
      />
      <Row>
        <Button size="sm" onClick={send} disabled={busy || !draft.trim()}>
          Send
        </Button>
      </Row>
    </div>
  );
};

// ---------------------------------------------------------------- New workflow

const NewWorkflow: React.FC = () => {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const orgId = user?.organizationId || '';
  const { data: allAgents = [] } = useGetAgentsQuery({ orgId }, { skip: !orgId });
  const agents = allAgents.filter((agent) => agent.scope !== 'system');
  const [create, { isLoading }] = useCreateWorkflowMutation();
  const [proposal, setProposal] = useState<WorkflowDocument | null>(null);
  const [issues, setIssues] = useState<ReadinessIssue[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);

  const accept = async () => {
    if (!proposal) return;
    try {
      const saved = await create({ document: proposal, source: 'builder' }).unwrap();
      show({ message: 'Saved as a draft. Nothing is live yet.', type: 'success' });
      navigate(`/workflows/${saved.id}?tab=definition`);
    } catch (error) {
      show({ message: workflowErrorMessage(error), type: 'error' });
    }
  };

  const blocking = issues.filter((issue) => issue.severity !== 'warning');
  const warnings = issues.filter((issue) => issue.severity === 'warning');

  return (
    <Page>
      <Back to="/workflows">← Workflows</Back>
      <HeaderRow>
        <div>
          <Title>New workflow</Title>
          <Sub>Describe the job. Nothing is saved until you accept.</Sub>
        </div>
      </HeaderRow>
      {/* The Builder keeps its place in this tree so the conversation survives a proposal. */}
      <NewColumns $proposing={!!proposal}>
        {proposal && (
          <div>
            <WorkflowGraph document={proposal} selected={selected} onSelect={setSelected} />
            <Row>
              <Button onClick={accept} isLoading={isLoading}>
                Accept
              </Button>
              <Button variant="outline" onClick={() => setProposal(null)}>
                Discard
              </Button>
              <Button variant="ghost" onClick={() => setShowJson(true)}>
                Proposed JSON
              </Button>
            </Row>
          </div>
        )}
        <Panel padding={16} elevation="sm">
          {proposal && blocking.length > 0 && (
            <>
              <Heading>Still to fix before it can run</Heading>
              {blocking.map((issue, index) => (
                <Issue key={index} onClick={() => setSelected(issue.node_id)}>
                  {issue.message}
                  <small>{issue.next}</small>
                </Issue>
              ))}
            </>
          )}
          {proposal && warnings.length > 0 && (
            <>
              <Heading>Worth checking</Heading>
              {warnings.map((issue, index) => (
                <Body key={index}>{issue.message}</Body>
              ))}
            </>
          )}
          <Builder
            chosenAgents={chosen}
            onProposal={(document, turn) => {
              if (document) setProposal(document);
              setIssues(turn?.issues ?? []);
            }}
          />
        </Panel>
        {!proposal && (
          <Panel padding={16} elevation="sm">
            <Heading>Your agents</Heading>
            {agents.length === 0 && <Body>No agents yet. Describing the job still works.</Body>}
            {agents.slice(0, 30).map((agent) => {
              const name = agent.agentName || agent.name;
              const using = chosen.includes(name);
              return (
                <AgentRow key={agent.id}>
                  <div>
                    {name}
                    <small>{agent.description}</small>
                  </div>
                  <Button
                    size="sm"
                    variant={using ? 'secondary' : 'outline'}
                    onClick={() =>
                      setChosen((list) =>
                        using ? list.filter((item) => item !== name) : [...list, name],
                      )
                    }
                  >
                    {using ? 'Using' : 'Use'}
                  </Button>
                </AgentRow>
              );
            })}
            <Row>
              <Button size="sm" variant="ghost" onClick={() => navigate('/agents/new')}>
                Create new agent
              </Button>
            </Row>
            <Body>Draft agents can be tested. They need publishing before a workflow goes live.</Body>
          </Panel>
        )}
      </NewColumns>
      <Modal isOpen={showJson} onClose={() => setShowJson(false)} title="Proposed JSON">
        <Pre style={{ maxHeight: 480 }}>{pretty(proposal)}</Pre>
      </Modal>
    </Page>
  );
};

// ---------------------------------------------------------------- Run dialog

const RunDialog: React.FC<{
  workflow: WorkflowDetailData;
  isOpen: boolean;
  onClose: () => void;
  onStarted: (runId: string) => void;
  initialInput?: unknown;
}> = ({ workflow, isOpen, onClose, onStarted, initialInput }) => {
  const { show } = useToast();
  const [start, { isLoading }] = useStartWorkflowRunMutation();
  const [kind, setKind] = useState<'test' | 'live'>('test');
  const [input, setInput] = useState('');
  const [files, setFiles] = useState('');
  const canLive = workflow.lifecycle === 'active';
  const everLive = workflow.active_version !== null;
  const contract = workflow.definition?.input;

  useEffect(() => {
    if (isOpen) {
      setKind('test');
      setInput(initialInput === undefined ? '' : pretty(initialInput));
      setFiles('');
    }
  }, [isOpen, initialInput]);

  const submit = async () => {
    let parsed: unknown = input.trim() || undefined;
    if (typeof parsed === 'string' && /^[[{]/.test(parsed)) {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        show({ message: 'The input looks like JSON, and it does not parse.', type: 'error' });
        return;
      }
    }
    try {
      const run = await start({
        id: workflow.id,
        kind,
        input: parsed,
        attachments: files
          .split(/[\s,]+/)
          .map((item) => item.trim())
          .filter(Boolean),
      }).unwrap();
      onClose();
      onStarted(run.id);
    } catch (error) {
      const issues = workflowErrorIssues(error);
      show({
        message: issues[0]?.message || workflowErrorMessage(error),
        type: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Run">
      <Heading>{contract?.description || 'Input'}</Heading>
      <TextArea
        aria-label="Input"
        value={input}
        placeholder={
          contract?.schema ? 'JSON matching what this workflow needs' : 'Text, or JSON'
        }
        onChange={(event) => setInput(event.target.value)}
      />
      {contract?.attachments && (
        <>
          <Heading>{contract.attachments.label || 'Attachments'}</Heading>
          <Input
            aria-label="File ids"
            placeholder="file_… ids, from an upload"
            value={files}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFiles(event.target.value)}
            fullWidth
          />
        </>
      )}
      <Heading>Test or live</Heading>
      <NativeSelect
        aria-label="Test or live"
        value={kind}
        onChange={(event) => setKind(event.target.value as 'test' | 'live')}
      >
        <option value="test">Test: the latest saved version</option>
        {everLive && (
          <option value="live" disabled={!canLive}>
            {canLive ? 'Live: the active version' : 'Live: resume this workflow first'}
          </option>
        )}
      </NativeSelect>
      <Callout variant="warning" title="This is real">
        {TEST_WARNING}
      </Callout>
      <Row>
        <Button onClick={submit} isLoading={isLoading}>
          {kind === 'live' ? 'Run live' : 'Run test'}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </Row>
    </Modal>
  );
};

// ---------------------------------------------------------------- Runs tab

const StepDetail: React.FC<{
  step: RunStep;
  run: RunDetail;
  workflowId: string;
  node?: WorkflowNode;
  onFix: (nodeId: string) => void;
}> = ({ step, run, workflowId, node, onFix }) => {
  const { show } = useToast();
  const [respond, { isLoading: responding }] = useRespondToWorkflowRunMutation();
  const [ask, { isLoading: asking }] = useAskWorkflowRunMutation();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const latest = step.passes[0];
  const waiting = run.status === 'waiting' && step.status === 'waiting';
  const isApproval = node?.wait?.kind === 'approval';

  const answerWait = async (value: 'approve' | 'reject' | 'reply') => {
    try {
      await respond({
        id: workflowId,
        runId: run.id,
        answer: value,
        payload: value === 'reply' ? { text: reply } : undefined,
      }).unwrap();
    } catch (error) {
      show({ message: workflowErrorMessage(error), type: 'error' });
    }
  };

  const submitQuestion = async () => {
    if (!question.trim()) return;
    try {
      const result = await ask({ id: workflowId, runId: run.id, question }).unwrap();
      setAnswer(
        result.answer + (result.steps?.length ? `\n\nSteps: ${result.steps.join(', ')}` : ''),
      );
    } catch (error) {
      setAnswer(workflowErrorMessage(error));
    }
  };

  return (
    <Panel padding={16} elevation="sm">
      <Heading>{step.title || step.id}</Heading>
      {!step.taken && <Body>{step.reason || 'This step has not run.'}</Body>}
      {latest?.error && (
        <>
          <Heading>What stopped it</Heading>
          <Body>{latest.error}</Body>
          <Heading>What you can do</Heading>
          <Body>{latest.next}</Body>
          <Row>
            <Button size="sm" variant="outline" onClick={() => onFix(step.id)}>
              Fix this step
            </Button>
          </Row>
        </>
      )}
      {waiting && isApproval && (
        <>
          <Heading>Waiting for approval</Heading>
          <Body>{node?.wait?.prompt || 'Should this go ahead?'}</Body>
          <Row>
            <Button size="sm" onClick={() => answerWait('approve')} isLoading={responding}>
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => answerWait('reject')}>
              Reject
            </Button>
          </Row>
        </>
      )}
      {waiting && !isApproval && (
        <>
          <Heading>Waiting for {node?.wait?.event}</Heading>
          <TextArea
            aria-label="Answer for them"
            value={reply}
            placeholder="Or answer here on their behalf"
            onChange={(event) => setReply(event.target.value)}
          />
          <Row>
            <Button
              size="sm"
              variant="outline"
              onClick={() => answerWait('reply')}
              disabled={!reply.trim()}
            >
              Send reply
            </Button>
          </Row>
        </>
      )}
      {latest && (
        <>
          {latest.input != null && (
            <>
              <Heading>What it received</Heading>
              <Pre>{pretty(latest.input)}</Pre>
            </>
          )}
          {latest.output != null && (
            <>
              <Heading>What it handed on</Heading>
              <Pre>{pretty(latest.output)}</Pre>
            </>
          )}
          {latest.branch && (
            <>
              <Heading>Way out taken</Heading>
              <Body>{latest.branch}</Body>
            </>
          )}
          {latest.agent_name && (
            <Body>
              {latest.agent_name}
              {latest.agent_version ? ` ${latest.agent_version}` : ''}
            </Body>
          )}
          {latest.exchange_id && latest.session_id && latest.agent_name && (
            <Row>
              <Link to={`/agents/${latest.agent_name}/sessions/${latest.session_id}`}>
                Show the exchange
              </Link>
            </Row>
          )}
        </>
      )}
      {step.passes.length > 1 && (
        <>
          <Heading>Earlier on this run</Heading>
          {step.passes.slice(1).map((item) => (
            <Body key={`${item.pass_no}-${item.attempt}`}>
              Pass {item.pass_no}: {item.status}
              {item.branch ? `, took ${item.branch}` : ''}
              {item.error_code ? `, ${item.error_code}` : ''}
            </Body>
          ))}
        </>
      )}
      <Heading>Ask about this run</Heading>
      <TextArea
        aria-label="Ask about this run"
        value={question}
        onChange={(event) => setQuestion(event.target.value)}
        placeholder="It answers from this run's record, and changes nothing"
      />
      <Row>
        <Button size="sm" variant="outline" onClick={submitQuestion} isLoading={asking}>
          Ask
        </Button>
      </Row>
      {answer && <Message $mine={false}>{answer}</Message>}
    </Panel>
  );
};

const RunsTab: React.FC<{
  workflow: WorkflowDetailData;
  selectedRun: string | null;
  onSelectRun: (id: string) => void;
  onRunAgain: (input: unknown) => void;
  onFix: (nodeId: string) => void;
}> = ({ workflow, selectedRun, onSelectRun, onRunAgain, onFix }) => {
  const { show } = useToast();
  const { data: runs = [], isLoading, refetch: refetchRuns } = useGetWorkflowRunsQuery(workflow.id);
  const runId = selectedRun || runs[0]?.id || null;
  const {
    data: run,
    refetch,
  } = useGetWorkflowRunQuery({ id: workflow.id, runId: runId || '' }, { skip: !runId });
  const [cancel] = useCancelWorkflowRunMutation();
  const [step, setStep] = useState<string | null>(null);
  const { subscribe, unsubscribe, addListener, removeListener } = useWebSocketContext();
  const active = run ? ACTIVE.includes(run.status) : false;

  // Live progress. The channel is only returned to someone who could read the run.
  useEffect(() => {
    if (!run?.events_channel || !active) return undefined;
    subscribe(run.events_channel);
    const listener = (event: { type?: string; event_type?: string }) => {
      const type = event.type || event.event_type || '';
      if (type.startsWith('workflow.')) {
        refetch();
        refetchRuns();
      }
    };
    addListener(listener as never);
    return () => {
      removeListener(listener as never);
      unsubscribe();
    };
  }, [run?.events_channel, active]); // eslint-disable-line react-hooks/exhaustive-deps

  // A dropped socket must not freeze the screen, so a slow poll runs as well.
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => {
      refetch();
      refetchRuns();
    }, 4000);
    return () => clearInterval(timer);
  }, [active, refetch, refetchRuns]);

  const paint = useMemo(() => {
    const result: Record<string, StepPaint> = {};
    run?.steps.forEach((item) => {
      result[item.id] = {
        status: item.status,
        passes: new Set(item.passes.map((p) => p.pass_no)).size,
      };
    });
    return result;
  }, [run]);

  const shownStep =
    run?.steps.find((item) => item.id === step) || (run ? stepThatMatters(run.steps) : undefined);

  if (isLoading) return <Shimmer height="240px" />;
  if (!runs.length) {
    return (
      <EmptyState
        title="No runs yet"
        description="Use Run to try it. A test uses the latest saved version."
      />
    );
  }
  return (
    <div>
      <RunBar>
        <div>
          <NativeSelect
            aria-label="Run"
            value={runId || ''}
            onChange={(event) => {
              setStep(null);
              onSelectRun(event.target.value);
            }}
          >
            {runs.map((item) => (
              <option key={item.id} value={item.id}>
                {new Date(item.queued_at || '').toLocaleString()} · {item.kind} · {item.status}
              </option>
            ))}
          </NativeSelect>
          {run && (
            <>
              <Verdict>{runVerdict(run)}</Verdict>
              <Sub>
                Version {run.version ?? 'draft'} · started by {run.trigger?.kind}
                {run.usage
                  ? ` · ${run.usage.input_tokens + run.usage.output_tokens} tokens · $${Number(
                      run.usage.platform_cost_usd || 0,
                    ).toFixed(4)}`
                  : ''}
              </Sub>
            </>
          )}
        </div>
        {run && (
          <Actions>
            {active ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  cancel({ id: workflow.id, runId: run.id })
                    .unwrap()
                    .then(() => show({ message: 'It stops after the current step.', type: 'info' }))
                    .catch((error) => show({ message: workflowErrorMessage(error), type: 'error' }))
                }
              >
                Cancel run
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => onRunAgain(run.input)}>
                Run again
              </Button>
            )}
          </Actions>
        )}
      </RunBar>
      {run && (
        <Columns>
          <WorkflowGraph
            document={run.definition}
            paint={paint}
            selected={shownStep?.id}
            onSelect={setStep}
          />
          {shownStep && (
            <StepDetail
              step={shownStep}
              run={run}
              workflowId={workflow.id}
              node={run.definition.nodes.find((item) => item.id === shownStep.id)}
              onFix={onFix}
            />
          )}
        </Columns>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Definition tab

const StepForm: React.FC<{
  node: WorkflowNode;
  document: WorkflowDocument;
  readOnly: boolean;
  onChange: (next: WorkflowDocument) => void;
  onClose: () => void;
}> = ({ node, document, readOnly, onChange, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId || '';
  const { data: allAgents = [] } = useGetAgentsQuery({ orgId }, { skip: !orgId });
  const agents = allAgents.filter((agent) => agent.scope !== 'system');
  const update = (changes: Partial<WorkflowNode>) =>
    onChange({
      ...document,
      nodes: document.nodes.map((item) => (item.id === node.id ? { ...item, ...changes } : item)),
    });
  const ways = document.edges.filter((edge) => edge.from === node.id);
  const remove = () =>
    onChange({
      ...document,
      nodes: document.nodes.filter((item) => item.id !== node.id),
      edges: document.edges.filter((edge) => edge.from !== node.id && edge.to !== node.id),
    });
  const addNext = () => {
    let index = 1;
    while (document.nodes.some((item) => item.id === `step_${index}`)) index += 1;
    const id = `step_${index}`;
    onChange({
      ...document,
      nodes: [
        ...document.nodes,
        { id, type: 'agent', title: 'New step', agent: null, input: { instruction: '' } },
      ],
      edges: [
        ...document.edges.filter((edge) => edge.from !== node.id),
        { from: node.id, to: id },
        ...ways.map((edge) => ({ ...edge, from: id })),
      ],
    });
  };

  return (
    <div>
      <Button size="sm" variant="ghost" onClick={onClose}>
        ← Whole workflow
      </Button>
      <Heading>{node.type === 'trigger' ? 'How it starts' : `${node.type} step`}</Heading>
      {node.type === 'trigger' && <Body>{triggerWords(document)} Change it in View JSON.</Body>}
      {node.type !== 'trigger' && (
        <>
          <Input
            label="Name"
            value={node.title || ''}
            disabled={readOnly}
            fullWidth
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              update({ title: event.target.value })
            }
          />
          {node.type === 'agent' && (
            <>
              <Heading>Agent</Heading>
              <NativeSelect
                aria-label="Agent"
                disabled={readOnly}
                value={node.agent?.name || ''}
                onChange={(event) =>
                  update({ agent: event.target.value ? { name: event.target.value } : null })
                }
              >
                <option value="">Needs an agent</option>
                {node.agent?.name &&
                  !agents.some((a) => (a.agentName || a.name) === node.agent?.name) && (
                    <option value={node.agent.name}>{node.agent.name}</option>
                  )}
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.agentName || agent.name}>
                    {agent.agentName || agent.name}
                  </option>
                ))}
              </NativeSelect>
              {!readOnly && (
                <Row>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate(
                        `/agents/new?instructions=${encodeURIComponent(agentBrief(node))}`,
                      )
                    }
                  >
                    Create new agent
                  </Button>
                </Row>
              )}
              <Heading>What it is told to do</Heading>
              <TextArea
                aria-label="Instruction"
                disabled={readOnly}
                value={node.input?.instruction || ''}
                onChange={(event) =>
                  update({ input: { ...node.input, instruction: event.target.value } })
                }
              />
              <Heading>What it does</Heading>
              <Input
                aria-label="Purpose"
                value={node.purpose || ''}
                disabled={readOnly}
                fullWidth
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update({ purpose: event.target.value })
                }
              />
              <Heading>What it hands on</Heading>
              <Input
                aria-label="Output"
                value={node.output?.description || ''}
                disabled={readOnly}
                fullWidth
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update({ output: { ...node.output, description: event.target.value } })
                }
              />
            </>
          )}
          {node.type === 'decision' && (
            <>
              <Heading>What it decides</Heading>
              <Body>{node.decision?.question}</Body>
              <Body>
                {node.decision?.mode === 'agent'
                  ? `Answered by ${node.decision.agent?.name || 'an agent'}.`
                  : 'Answered by rules. No tokens.'}
              </Body>
            </>
          )}
        </>
      )}
      <Heading>Ways out</Heading>
      {ways.length === 0 && <Body>None.</Body>}
      {ways.map((edge) => (
        <Body key={`${edge.to}-${edge.when}`}>
          {edge.when ? `${edge.when} → ` : '→ '}
          {edge.to}
          {edge.loop ? ` (at most ${edge.max_passes ?? '?'} times, then the run stops)` : ''}
        </Body>
      ))}
      {!readOnly && (
        <Row>
          {node.type !== 'end' && (
            <Button size="sm" variant="outline" onClick={addNext}>
              + Add next step
            </Button>
          )}
          {node.type !== 'trigger' && (
            <Button size="sm" variant="ghost" onClick={remove}>
              Remove step
            </Button>
          )}
        </Row>
      )}
    </div>
  );
};

const DefinitionTab: React.FC<{
  workflow: WorkflowDetailData;
  focus: string | null;
  setFocus: (id: string | null) => void;
}> = ({ workflow, focus, setFocus }) => {
  const { show } = useToast();
  const [save] = useSaveWorkflowDraftMutation();
  const [restore] = useRestoreWorkflowVersionMutation();
  const { data: history } = useGetWorkflowVersionsQuery(workflow.id);
  const [proposal, setProposal] = useState<WorkflowDocument | null>(null);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const readOnly = workflow.lifecycle === 'archived';
  const shown = proposal || workflow.definition;
  const issues = workflow.readiness?.issues ?? [];

  const saveDocument = async (document: WorkflowDocument, source?: 'builder') => {
    try {
      return await save({
        id: workflow.id,
        document,
        base_hash: workflow.content_hash || '',
        source,
      }).unwrap();
    } catch (error) {
      const found = workflowErrorIssues(error);
      setJsonError(found[0]?.message || workflowErrorMessage(error));
      show({ message: found[0]?.message || workflowErrorMessage(error), type: 'error' });
      return null;
    }
  };

  if (!shown) return <EmptyState title="No definition yet" />;
  const node = shown.nodes.find((item) => item.id === focus);

  return (
    <Columns>
      <div>
        <WorkflowGraph document={shown} selected={focus} onSelect={setFocus} />
        {proposal && (
          <Row>
            <Button
              onClick={async () => {
                if (await saveDocument(proposal, 'builder')) {
                  setProposal(null);
                  show({ message: 'Saved to the draft. The live version is unchanged.', type: 'success' });
                }
              }}
            >
              Accept
            </Button>
            <Button variant="outline" onClick={() => setProposal(null)}>
              Discard
            </Button>
          </Row>
        )}
        <Row>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setJsonText(JSON.stringify(shown, null, 2));
              setJsonError(null);
              setJsonOpen(true);
            }}
          >
            View JSON
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(true)}>
            History
          </Button>
        </Row>
      </div>
      <Panel padding={16} elevation="sm">
        {node ? (
          <StepForm
            node={node}
            document={shown}
            readOnly={readOnly || Boolean(proposal)}
            onClose={() => setFocus(null)}
            onChange={async (next) => {
              const removed = !next.nodes.some((item) => item.id === node.id);
              if (removed) setFocus(null);
              const saved = await saveDocument(next);
              if (removed && saved) {
                // The save above moved the draft on, so Undo builds on that hash.
                show(
                  undoToast('Step removed from the draft.', () =>
                    save({
                      id: workflow.id,
                      document: shown,
                      base_hash: saved.content_hash || '',
                    }),
                  ),
                );
              }
            }}
          />
        ) : (
          <>
            <Heading>Readiness</Heading>
            {issues.length === 0 ? (
              <Body>Ready to run</Body>
            ) : (
              issues.map((issue, index) => (
                <Issue key={index} onClick={() => setFocus(issue.node_id)}>
                  {issue.message}
                  <small>{issue.next}</small>
                </Issue>
              ))
            )}
            {(workflow.readiness?.warnings ?? []).map((issue, index) => (
              <Body key={index}>{issue.message}</Body>
            ))}
            <Heading>Applications</Heading>
            {workflow.applications.length === 0 ? (
              <Body>
                None. It can still run from Run, a schedule, or an event. Add it from an
                application's Workflows tab.
              </Body>
            ) : (
              workflow.applications.map((app) => (
                <Body key={app.app_id}>
                  <Link to={`/applications/${app.app_id}?tab=workflows`}>{app.name}</Link>
                </Body>
              ))
            )}
          </>
        )}
        {!readOnly && (
          <div style={{ marginTop: 16 }}>
            <Builder
              workflow={workflow}
              focus={focus}
              onProposal={(document) => document && setProposal(document)}
            />
          </div>
        )}
      </Panel>
      <Modal isOpen={jsonOpen} onClose={() => setJsonOpen(false)} title="Workflow JSON">
        <CodeArea
          aria-label="Workflow JSON"
          value={jsonText}
          readOnly={readOnly}
          onChange={(event) => setJsonText(event.target.value)}
        />
        {jsonError && <Callout variant="error" title="Not saved">{jsonError}</Callout>}
        <Body>
          POST /v1/workflows/{workflow.id}/runs starts it from the Direct API. The calling
          application must have this workflow added.
        </Body>
        {!readOnly && (
          <Row>
            <Button
              onClick={async () => {
                let parsed: WorkflowDocument;
                try {
                  parsed = JSON.parse(jsonText);
                } catch {
                  setJsonError('This is not valid JSON.');
                  return;
                }
                if (await saveDocument(parsed)) {
                  setJsonOpen(false);
                  show({ message: 'Saved to the draft.', type: 'success' });
                }
              }}
            >
              Save draft
            </Button>
          </Row>
        )}
      </Modal>
      <Modal isOpen={historyOpen} onClose={() => setHistoryOpen(false)} title="History">
        {(history?.versions ?? []).map((item) => (
          <AgentRow key={item.version_id}>
            <div>
              {new Date(item.saved_at).toLocaleString()}
              <small>
                {item.version ? `Version ${item.version}, ${item.state}` : 'Earlier save'} ·{' '}
                {item.save_source}
              </small>
            </div>
            {!readOnly && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  restore({ id: workflow.id, versionId: item.version_id })
                    .unwrap()
                    .then(() => {
                      setHistoryOpen(false);
                      show({ message: 'Restored as a new draft. Nothing went live.', type: 'success' });
                    })
                    .catch((error) => show({ message: workflowErrorMessage(error), type: 'error' }))
                }
              >
                Restore
              </Button>
            )}
          </AgentRow>
        ))}
      </Modal>
    </Columns>
  );
};

// ---------------------------------------------------------------- The screen

const ExistingWorkflow: React.FC<{ id: string }> = ({ id }) => {
  const { show } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: workflow, isLoading, isError } = useGetWorkflowQuery(id);
  const [setStatus, { isLoading: changing }] = useSetWorkflowStatusMutation();
  const [discard] = useDiscardWorkflowDraftMutation();
  const [restoreVersion] = useRestoreWorkflowVersionMutation();
  const [runOpen, setRunOpen] = useState(false);
  const [runInput, setRunInput] = useState<unknown>(undefined);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewIssues, setReviewIssues] = useState<ReadinessIssue[]>([]);
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const tab = searchParams.get('tab') === 'definition' ? 'definition' : 'runs';
  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  if (isLoading) {
    return (
      <Page>
        <Shimmer height="320px" />
      </Page>
    );
  }
  if (isError || !workflow) {
    return (
      <Page>
        <EmptyState
          title="Workflow not found"
          action={<Button onClick={() => navigate('/workflows')}>Back to Workflows</Button>}
        />
      </Page>
    );
  }

  const change = async (action: string, message: string, undoAction?: string) => {
    try {
      await setStatus({ id, action }).unwrap();
      show(
        undoAction
          ? undoToast(message, () => setStatus({ id, action: undoAction }))
          : { message, type: 'success' },
      );
    } catch (error) {
      const issues = workflowErrorIssues(error);
      show({ message: issues[0]?.message || workflowErrorMessage(error), type: 'error' });
    }
  };

  const activate = async () => {
    setReviewIssues([]);
    try {
      await setStatus({ id, action: 'activate', content_hash: workflow.content_hash || '' }).unwrap();
      setReviewOpen(false);
      show({ message: `Version ${workflow.draft?.version} is live.`, type: 'success' });
    } catch (error) {
      const issues = workflowErrorIssues(error);
      if (issues.length) setReviewIssues(issues);
      else show({ message: workflowErrorMessage(error), type: 'error' });
    }
  };

  const discardDraft = async () => {
    try {
      const result = await discard(id).unwrap();
      show(
        undoToast('Draft discarded. Back to the live version.', () =>
          restoreVersion({ id, versionId: result.discarded_version_id }),
        ),
      );
    } catch (error) {
      show({ message: workflowErrorMessage(error), type: 'error' });
    }
  };

  const isArchived = workflow.lifecycle === 'archived';
  const badge = LIFECYCLE_BADGES[workflow.lifecycle];
  const blockers = workflow.readiness?.issues ?? [];

  return (
    <Page>
      <Back to="/workflows">← Workflows</Back>
      <HeaderRow>
        <div>
          <Title>{workflow.title || workflow.name}</Title>
          <Sub>
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>{' '}
            {triggerWords(workflow.active_definition || workflow.definition)}
          </Sub>
        </div>
        <Actions>
          {workflow.lifecycle === 'active' && (
            <Button variant="outline" onClick={() => change('pause', 'Paused. Schedules and events will not start it.')} disabled={changing}>
              Pause
            </Button>
          )}
          {workflow.lifecycle === 'paused' && (
            <Button variant="outline" onClick={() => change('resume', 'Resumed on the live version.')} disabled={changing}>
              Resume
            </Button>
          )}
          {isArchived && (
            <Button variant="outline" onClick={() => change('restore', 'Restored. It is not live until you resume it.')} disabled={changing}>
              Restore
            </Button>
          )}
          {!isArchived && (
            <Button variant="ghost" onClick={() => change('archive', 'Archived.', 'restore')} disabled={changing}>
              Archive
            </Button>
          )}
          {!isArchived && (
            <Button
              onClick={() => {
                setRunInput(undefined);
                setRunOpen(true);
              }}
            >
              Run
            </Button>
          )}
        </Actions>
      </HeaderRow>

      {workflow.draft && !isArchived && (
        <Bar>
          <span>
            {workflow.draft_ahead
              ? `Draft ahead of Version ${workflow.active_version}. Live runs still use Version ${workflow.active_version}.`
              : 'Not live yet.'}
          </span>
          <Actions>
            {workflow.draft_ahead && (
              <Button size="sm" variant="ghost" onClick={discardDraft}>
                Discard draft
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                setReviewIssues([]);
                setReviewOpen(true);
              }}
            >
              Review and activate
            </Button>
          </Actions>
        </Bar>
      )}

      <Tabs
        items={[
          { id: 'runs', label: 'Runs' },
          { id: 'definition', label: 'Definition' },
        ]}
        activeTab={tab}
        onChange={setTab}
        variant="underline"
      />
      <div style={{ marginTop: 16 }}>
        {tab === 'runs' ? (
          <RunsTab
            workflow={workflow}
            selectedRun={selectedRun}
            onSelectRun={setSelectedRun}
            onRunAgain={(input) => {
              setRunInput(input);
              setRunOpen(true);
            }}
            onFix={(nodeId) => {
              setFocus(nodeId);
              setTab('definition');
            }}
          />
        ) : (
          <DefinitionTab workflow={workflow} focus={focus} setFocus={setFocus} />
        )}
      </div>

      <RunDialog
        workflow={workflow}
        isOpen={runOpen}
        initialInput={runInput}
        onClose={() => setRunOpen(false)}
        onStarted={(runId) => {
          setSelectedRun(runId);
          setTab('runs');
        }}
      />
      <Modal isOpen={reviewOpen} onClose={() => setReviewOpen(false)} title="Review and activate">
        {blockers.length > 0 || reviewIssues.length > 0 ? (
          <>
            <Heading>This cannot go live yet</Heading>
            {[...blockers, ...reviewIssues].map((issue, index) => (
              <Body key={index}>
                {issue.message} {issue.next}
              </Body>
            ))}
          </>
        ) : (
          <>
            <Heading>What changes in Version {workflow.draft?.version}</Heading>
            {documentChanges(workflow.active_definition, workflow.definition).map((line) => (
              <Body key={line}>{line}</Body>
            ))}
            <Body>Runs already going keep the version they started on.</Body>
            <Row>
              <Button onClick={activate} isLoading={changing}>
                Activate
              </Button>
              <Button variant="outline" onClick={() => setReviewOpen(false)}>
                Not yet
              </Button>
            </Row>
          </>
        )}
      </Modal>
    </Page>
  );
};

const WorkflowDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <ExistingWorkflow id={id} /> : <NewWorkflow />;
};

export default WorkflowDetail;
